import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  useTheme,
} from '@mui/material';
import { motion } from 'framer-motion';

// Import local brand logos
import SKFLogo from '../images/skf.jpeg';
import NachiLogo from '../images/NachiEdite.png';
import HIWINLogo from '../images/HIWINEdited.png';
import JAFLogo from '../images/JAFEdited.png';
import AsahiLogo from '../images/asahi.jpeg';
import NTNLogo from '../images/ntn.jpeg';
import NAKLogo from '../images/nak.jpeg';
import NKELogo from '../images/nke.jpeg';
import NSKLogo from '../images/nsk.jpeg';
import FYHLogo from '../images/fyh.jpeg';
import TimkenLogo from '../images/timken.jpeg';
import TTOLogo from '../images/tto.jpeg';

const MotionBox = motion(Box);
const MotionTypography = motion(Typography);

const brands = [
  { 
    name: 'SKF', 
    logo: SKFLogo,
    color: '#005bab'
  },
  { 
    name: 'NACHI', 
    logo: NachiLogo,
    color: '#d32f2f'
  },
  { 
    name: 'HIWIN', 
    logo: HIWINLogo,
    color: '#e53935'
  },
  { 
    name: 'JAF', 
    logo: JAFLogo,
    color: '#1565c0'
  },
  { 
    name: 'ASAHI', 
    logo: AsahiLogo,
    color: '#0056a6'
  },
  { 
    name: 'NTN', 
    logo: NTNLogo,
    color: '#003d7a'
  },
  { 
    name: 'NAK', 
    logo: NAKLogo,
    color: '#1a237e'
  },
  { 
    name: 'NKE', 
    logo: NKELogo,
    color: '#0d47a1'
  },
  { 
    name: 'NSK', 
    logo: NSKLogo,
    color: '#00695c'
  },
  { 
    name: 'FYH', 
    logo: FYHLogo,
    color: '#c62828'
  },
  { 
    name: 'TIMKEN', 
    logo: TimkenLogo,
    color: '#ff6f00'
  },
  { 
    name: 'TTO', 
    logo: TTOLogo,
    color: '#283593'
  },
];

const Brands = () => {
  const theme = useTheme();

  return (
    <Box
      id="brands"
      sx={{
        py: { xs: 8, md: 15 },
        bgcolor: theme.palette.mode === 'dark' ? '#0a0a0a' : 'background.paper',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background Graphic */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '100%',
          opacity: 0.05,
          backgroundImage: `linear-gradient(45deg, #333 25%, transparent 25%, transparent 75%, #333 75%, #333), 
                           linear-gradient(45deg, #333 25%, transparent 25%, transparent 75%, #333 75%, #333)`,
          backgroundSize: '60px 60px',
          backgroundPosition: '0 0, 30px 30px',
          zIndex: 0,
          display: theme.palette.mode === 'dark' ? 'block' : 'none',
        }}
      />

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ textAlign: 'center', mb: 10 }}>
          <MotionTypography
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            variant="subtitle1"
            sx={{
              color: '#e57373',
              fontFamily: '"Rajdhani", sans-serif',
              fontWeight: 700,
              letterSpacing: 3,
              textTransform: 'uppercase',
              mb: 2,
            }}
          >
            OUR PARTNERS & AUTHORIZED BRANDS
          </MotionTypography>
          <MotionTypography
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            variant="h2"
            sx={{
              fontFamily: '"Orbitron", sans-serif',
              fontSize: { xs: '1.8rem', md: '3rem' },
              fontWeight: 700,
              color: theme.palette.mode === 'dark' ? 'white' : 'text.primary',
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              textShadow: theme.palette.mode === 'dark' ? '0 0 30px rgba(229, 115, 115, 0.3)' : 'none',
              mb: 4
            }}
          >
            OFFICIAL AGENCIES & TRUSTED INDUSTRIAL PARTNERS
          </MotionTypography>
          
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            gap: { xs: 2, md: 4 }, 
            flexWrap: 'wrap',
            maxWidth: '900px',
            mx: 'auto',
            mb: 5
          }}>
             {[
               "Official Authorized industrial brands",
               "Trusted global manufacturers",
               "Certified quality & genuine products",
               "Long-term partnership & reliability"
             ].map((text, index) => (
                <MotionTypography
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.3 + (index * 0.1) }}
                  variant="body1"
                  sx={{
                    fontFamily: '"Rajdhani", sans-serif',
                    fontSize: { xs: '0.9rem', md: '1.1rem' },
                    fontWeight: 600,
                    color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'text.secondary',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <span style={{ color: '#e57373', fontWeight: 'bold' }}>~</span> {text}
                </MotionTypography>
             ))}
          </Box>

          <Box sx={{ width: 80, height: 4, bgcolor: '#e57373', mx: 'auto', mt: 4, borderRadius: 2 }} />
        </Box>

        <Grid container spacing={4} justifyContent="center">
          {brands.map((brand, index) => (
            <Grid item xs={6} sm={4} md={3} lg={2.4} key={index}>
              <MotionBox
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -10 }}
                sx={{
                  height: 180,
                  bgcolor: theme.palette.mode === 'dark' ? '#151515' : 'white',
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 3,
                  cursor: 'pointer',
                  border: theme.palette.mode === 'dark' ? '1px solid #333' : '1px solid #eee',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    borderColor: brand.color,
                    boxShadow: `0 10px 30px -10px ${brand.color}`,
                    '& .brand-logo': {
                      transform: 'scale(1.1)',
                      filter: 'brightness(1.1)',
                    }
                  }
                }}
              >
                <Box
                  className="brand-logo"
                  component="img"
                  src={brand.logo}
                  alt={brand.name}
                  sx={{
                    maxWidth: '100%',
                    maxHeight: '100px',
                    objectFit: 'contain',
                    transition: 'all 0.4s ease',
                  }}
                />
              </MotionBox>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default Brands;
